import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";
import { adjustColorForColorblindness as adjustColor } from "../utils/colorAdjustment";
import {
  colorSchemes,
  DEFAULT_PAGE_BACKGROUND,
  DEFAULT_CARD_BACKGROUND,
} from "../constants/colorSchemes";
import {
  BACKGROUND_THEME_NONE,
  resolveBackgroundTheme,
} from "../constants/backgroundThemes";
import {
  getAppTextColor,
  getBackgroundColor,
  getReadableDisplayTextColor,
} from "../utils/themeColors";
import {
  DEFAULT_FONT,
  formatFontForCss,
  normalizeFontName,
} from "../utils/fonts";

/**
 * Interface defining theme properties and appearance settings
 */
interface Theme {
  /** Display name of the theme */
  name: string;
  /** Background color in hex format */
  backgroundColor: string;
  /** Text color in hex format */
  textColor: string;
  /** Original text color before adjustments */
  originalTextColor: string;
  /** User-selected full-screen text color */
  originalDisplayTextColor?: string;
  /** Original background color before adjustments */
  originalBackgroundColor: string;
  /** Optional CSS class name */
  className?: string;
  /** Display text color after adjustments */
  displayTextColor?: string;
  /** Display background color after adjustments */
  displayBackgroundColor?: string;
  /** Background color for collection cards and collection modals */
  cardBackgroundColor?: string;
  /** Optional background image URL */
  backgroundImage?: string;
  /** Flag for colorblind mode */
  isColorblindMode: boolean;
  /** Type of colorblindness adjustment */
  colorblindType: string;
  /** Main font family */
  font: string;
  /** Heading font family */
  headingFont: string;
  /** Button font family */
  buttonFont: string;
  /** Function to adjust colors for colorblindness */
  adjustColorForColorblindness: (color: string) => string;
  /** Display font family */
  displayFont: string;
}

/**
 * Interface defining theme context functionality
 */
interface ThemeContextType {
  /** Current theme settings */
  theme: Theme;
  /** Function to set the entire theme */
  setTheme: Dispatch<SetStateAction<Theme>>;
  /** Function to update specific theme properties */
  updateTheme: (newTheme: Theme) => void;
  /** Function to set display text color */
  setDisplayTextColor: (color: string) => void;
  /** Function to set display background color */
  setDisplayBackgroundColor: (color: string) => void;
  /** Function to set collection card and modal background color */
  setCardBackgroundColor: (color: string) => void;
  /** Function to toggle colorblind mode */
  setColorblindMode: (isEnabled: boolean) => void;
  /** Function to set colorblind type */
  setColorblindType: (type: string) => void;
  /** Function to adjust colors for colorblindness */
  adjustColorForColorblindness: (color: string) => string;
  /** Function to set main font */
  setFont: (font: string) => void;
  /** Function to set heading font */
  setHeadingFont: (font: string) => void;
  /** Function to set button font */
  setButtonFont: (font: string) => void;
  /** Function to set main text color */
  setMainTextColor: (color: string) => void;
  /** Function to set display font */
  setDisplayFont: (font: string) => void;
}

/** @deprecated Pass a complete theme object directly via setTheme instead. */
export const setThemeWithColorAdjustment = (newTheme: Theme) => newTheme;

const isPureWhite = (color?: string) =>
  !!color && ["#ffffff", "#fff", "white"].includes(color.trim().toLowerCase());

const withOffWhiteDefault = (theme: Theme): Theme => {
  const isWhiteTheme =
    theme.name === "White" ||
    (isPureWhite(theme.backgroundColor) &&
      isPureWhite(
        theme.originalBackgroundColor || theme.backgroundColor,
      ));

  if (isWhiteTheme) {
    return {
      ...theme,
      name: "White",
      backgroundColor: DEFAULT_PAGE_BACKGROUND,
      originalBackgroundColor: DEFAULT_PAGE_BACKGROUND,
      displayBackgroundColor: DEFAULT_PAGE_BACKGROUND,
    };
  }

  if (
    isPureWhite(theme.displayBackgroundColor) &&
    !isPureWhite(theme.backgroundColor)
  ) {
    return {
      ...theme,
      displayBackgroundColor: theme.backgroundColor,
      originalBackgroundColor: isPureWhite(theme.originalBackgroundColor)
        ? theme.backgroundColor
        : theme.originalBackgroundColor,
    };
  }

  return theme;
};

const normalizeTheme = (theme: Theme): Theme => {
  const backgroundColor = getBackgroundColor(theme);
  const appTextColor = getAppTextColor(theme);
  const storedDisplayTextColor = getReadableDisplayTextColor(theme);
  const font = normalizeFontName(theme.font);
  const headingFont = normalizeFontName(theme.headingFont || theme.font);
  const buttonFont = normalizeFontName(theme.buttonFont || theme.font);
  const displayFont = normalizeFontName(
    theme.displayFont || theme.font,
  );

  return withOffWhiteDefault({
    ...theme,
    font,
    headingFont,
    buttonFont,
    displayFont,
    originalTextColor: appTextColor,
    textColor: appTextColor,
    originalDisplayTextColor: storedDisplayTextColor,
    displayTextColor: storedDisplayTextColor,
    originalBackgroundColor: backgroundColor,
    displayBackgroundColor:
      theme.displayBackgroundColor || backgroundColor,
  });
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Provider component that manages theme state and appearance settings.
 * Handles color adjustments, font settings, and persistence in localStorage.
 * Supports colorblind modes and dynamic theme updates.
 */
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const getInitialTheme = (): Theme => {
    try {
      const savedTheme = localStorage.getItem("app-theme");
      if (savedTheme) {
        const parsedTheme = JSON.parse(savedTheme) as Theme;

        return normalizeTheme({
          ...parsedTheme,
          cardBackgroundColor:
            parsedTheme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
          backgroundImage: resolveBackgroundTheme(parsedTheme.backgroundImage),
          adjustColorForColorblindness: (color: string) =>
            parsedTheme.isColorblindMode
              ? adjustColor(color, parsedTheme.colorblindType)
              : color,
        });
      }
    } catch {
      console.error("Error loading theme");
    }

    const defaultScheme = colorSchemes[0];

    return normalizeTheme({
      ...defaultScheme,
      isColorblindMode: false,
      colorblindType: "protanopia",
      font: DEFAULT_FONT,
      headingFont: DEFAULT_FONT,
      buttonFont: DEFAULT_FONT,
      displayFont: DEFAULT_FONT,
      cardBackgroundColor: DEFAULT_CARD_BACKGROUND,
      backgroundImage: BACKGROUND_THEME_NONE,
      adjustColorForColorblindness: (color: string) => color,
    });
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme());

  const setDisplayTextColor = (color: string) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      displayTextColor: color,
      originalDisplayTextColor: color,
    }));
  };

  const setDisplayBackgroundColor = (color: string) => {
    setTheme((prevTheme) => ({ ...prevTheme, displayBackgroundColor: color }));
  };

  const setCardBackgroundColor = (color: string) => {
    setTheme((prevTheme) => ({ ...prevTheme, cardBackgroundColor: color }));
  };

  const updateTheme = (newTheme: Theme) => {
    setTheme(normalizeTheme(newTheme));
  };

  const setColorblindMode = (isEnabled: boolean) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      isColorblindMode: isEnabled,
    }));
  };

  const setColorblindType = (type: string) => {
    setTheme((prevTheme) => {
      const newAdjustColorForColorblindness = (color: string) =>
        adjustColor(color, type);

      return {
        ...prevTheme,
        colorblindType: type,
        adjustColorForColorblindness: newAdjustColorForColorblindness,
      };
    });
  };

  const setFont = (font: string) => {
    const normalized = normalizeFontName(font);
    setTheme((prevTheme) => ({ ...prevTheme, font: normalized }));
    document.documentElement.style.setProperty(
      "--font-family",
      formatFontForCss(normalized),
    );
  };

  const setHeadingFont = (font: string) => {
    const normalized = normalizeFontName(font);
    setTheme((prevTheme) => ({ ...prevTheme, headingFont: normalized }));
    document.documentElement.style.setProperty(
      "--heading-font-family",
      formatFontForCss(normalized),
    );
  };

  const setButtonFont = (font: string) => {
    const normalized = normalizeFontName(font);
    setTheme((prevTheme) => ({ ...prevTheme, buttonFont: normalized }));
    document.documentElement.style.setProperty(
      "--button-font-family",
      formatFontForCss(normalized),
    );
  };

  const setMainTextColor = (color: string) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      originalTextColor: color,
      textColor: color,
    }));
  };

  const setDisplayFont = (font: string) => {
    const normalized = normalizeFontName(font);
    setTheme((prevTheme) => ({ ...prevTheme, displayFont: normalized }));
    document.documentElement.style.setProperty(
      "--display-font-family",
      formatFontForCss(normalized),
    );
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    const backgroundColor = getBackgroundColor(theme);
    const appTextColor = getAppTextColor(theme);
    const storedDisplayTextColor = getReadableDisplayTextColor(theme);
    const displayBackgroundColor =
      theme.displayBackgroundColor || backgroundColor;

    const effectiveBackgroundColor = theme.isColorblindMode
      ? adjustColor(backgroundColor, theme.colorblindType)
      : backgroundColor;

    const effectiveDisplayTextColor = theme.isColorblindMode
      ? adjustColor(storedDisplayTextColor, theme.colorblindType)
      : storedDisplayTextColor;

    const pattern = resolveBackgroundTheme(theme.backgroundImage);

    // Always apply CSS vars on load + updates (fonts were previously skipped
    // on the first paint, which left the wrong default family until Settings).
    document.body.style.backgroundImage = "none";
    document.body.style.backgroundColor = effectiveBackgroundColor;
    document.body.style.color = appTextColor;

    document.documentElement.style.setProperty(
      "--background-color",
      effectiveBackgroundColor,
    );
    document.documentElement.style.setProperty(
      "--background-image",
      pattern === BACKGROUND_THEME_NONE ? "none" : `url(${pattern})`,
    );
    document.documentElement.style.setProperty("--text-color", appTextColor);
    document.documentElement.style.setProperty(
      "--display-background-color",
      displayBackgroundColor,
    );
    document.documentElement.style.setProperty(
      "--display-text-color",
      effectiveDisplayTextColor,
    );
    document.documentElement.style.setProperty(
      "--card-background-color",
      theme.isColorblindMode
        ? adjustColor(
            theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
            theme.colorblindType,
          )
        : theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
    );
    document.documentElement.style.setProperty(
      "--font-family",
      formatFontForCss(theme.font),
    );
    document.documentElement.style.setProperty(
      "--heading-font-family",
      formatFontForCss(theme.headingFont),
    );
    document.documentElement.style.setProperty(
      "--button-font-family",
      formatFontForCss(theme.buttonFont),
    );
    document.documentElement.style.setProperty(
      "--display-font-family",
      formatFontForCss(theme.displayFont || theme.font),
    );

    if (isInitialLoad.current) {
      // Persist migrated/normalized fonts so a hard refresh keeps the fix
      localStorage.setItem(
        "app-theme",
        JSON.stringify({
          ...theme,
          originalTextColor: appTextColor,
          textColor: appTextColor,
          originalDisplayTextColor: storedDisplayTextColor,
          displayTextColor: storedDisplayTextColor,
          originalBackgroundColor: backgroundColor,
          displayBackgroundColor,
          backgroundImage: pattern,
        }),
      );
      isInitialLoad.current = false;
      return;
    }

    localStorage.setItem(
      "app-theme",
      JSON.stringify({
        ...theme,
        originalTextColor: appTextColor,
        textColor: appTextColor,
        originalDisplayTextColor: storedDisplayTextColor,
        displayTextColor: storedDisplayTextColor,
        originalBackgroundColor: backgroundColor,
        displayBackgroundColor,
        backgroundImage: pattern,
      }),
    );
  }, [theme]);

  const themeContextValue: ThemeContextType = {
    theme,
    setTheme,
    updateTheme,
    setDisplayTextColor,
    setDisplayBackgroundColor,
    setCardBackgroundColor,
    setColorblindMode,
    setColorblindType,
    adjustColorForColorblindness: (color: string) =>
      theme.isColorblindMode ? adjustColor(color, theme.colorblindType) : color,
    setFont,
    setHeadingFont,
    setButtonFont,
    setMainTextColor,
    setDisplayFont,
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
